import React, { useState, useMemo } from 'react';
import { Input, Button, Modal } from 'antd';
import { DeleteOutlined, CheckOutlined, RightOutlined } from '@ant-design/icons';
import { ReadOnlyGuard } from '@spark';

const StepsModal = ({ visible, onCancel, title, subtitle, headerRightSlot = null, steps, currentStep, onStepChange, children, width = 600, centered = true, destroyOnClose = true, styles = {} }) => {
    const defaultStyles = { content: { width: `${width + 50}px`, maxWidth: `${width + 50}px`, margin: '0 auto', ...styles.content }, body: { ...styles.body } };
    return (
        <Modal open={visible} onCancel={onCancel} footer={null} width={width} centered={centered} destroyOnClose={destroyOnClose} styles={defaultStyles} zIndex={2000}>
            <div style={{ padding: 8 }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                    <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 2 }}>{title}</div>
                    {headerRightSlot}
                </div>
                {subtitle && <div style={{ color: '#6B7280', fontSize: 14, marginBottom: 18, fontWeight: 400 }}>{subtitle}</div>}
                <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', fontSize: 15, marginBottom: 24, rowGap: 8, columnGap: 0 }}>
                    {steps.map((step, index) => (
                        <React.Fragment key={index}>
                            <span style={{ color: currentStep === index ? '#1677FF' : '#111827', fontSize: 15, fontWeight: 300, cursor: onStepChange ? 'pointer' : 'default', display: 'inline-flex', alignItems: 'center', gap: 8, lineHeight: 1.4 }} onClick={() => onStepChange && onStepChange(index)}>
                                {index < currentStep ? <CheckOutlined style={{ color: '#1677FF', fontSize: 15, fontWeight: 700 }} /> : <span style={{ color: currentStep === index ? '#1677FF' : '#111827', fontWeight: 300 }}>{index + 1}.</span>}
                                <span>{step}</span>
                            </span>
                            {index < steps.length - 1 && <RightOutlined style={{ color: '#D1D5DB', fontSize: 15, margin: '0 14px' }} />}
                        </React.Fragment>
                    ))}
                </div>
                {children}
            </div>
        </Modal>
    );
};
import { sanitizeProjectName } from './projectNameUtils';
import TaskComposer from './task/TaskComposer';
import TaskModalScrollArea from './task/TaskModalScrollArea';
import { buildEmptyTask, buildTasksPayload } from './task/taskShape';
import { validateTask } from '@/features/tasks/utils/validators';
import { colors } from '@/shared/tokens/colors';

const { TextArea } = Input;

const buildEmptyProjectTask = (id) => buildEmptyTask({ id });

/**
 * Create-Project wizard.
 *
 * Step 1: project metadata (name + goal).
 * Step 2: one or more Tasks, each composed of one or more task types and
 *         rendered by the shared `TaskComposer`. Each Task Name becomes the
 *         `sub_group_name` shared by every task type inside that task.
 *
 * The same `TaskComposer` is reused by `NewTaskModal`, so any layout / field
 * change here (or there) automatically applies to both flows.
 */
export const CreateProjectModal = ({ isOpen, onClose, onCreateProject }) => {
    const [step, setStep] = useState(0);
    const [projectName, setProjectName] = useState('');
    const [projectGoal, setProjectGoal] = useState('');
    const [projectNameError, setProjectNameError] = useState(false);
    const [tasks, setTasks] = useState([buildEmptyProjectTask(1)]);
    const [taskErrors, setTaskErrors] = useState({});
    const [step2Summary, setStep2Summary] = useState('');

    const resetForm = () => {
        setStep(0);
        setProjectName('');
        setProjectGoal('');
        setProjectNameError(false);
        setTasks([buildEmptyProjectTask(1)]);
        setTaskErrors({});
        setStep2Summary('');
    };

    const handleNextStep = () => {
        if (!projectName.trim()) {
            setProjectNameError(true);
            return;
        }
        setProjectNameError(false);
        setStep(1);
    };

    const handlePrevStep = () => setStep(0);

    const addTask = () => {
        const nextId = tasks.length === 0 ? 1 : Math.max(...tasks.map((task) => task.id)) + 1;
        setTasks([...tasks, buildEmptyProjectTask(nextId)]);
    };

    const removeTask = (taskId) => {
        if (tasks.length <= 1) return;
        setTasks(tasks.filter((task) => task.id !== taskId));
        setTaskErrors((prev) => {
            if (!prev?.[taskId]) return prev;
            const next = { ...prev };
            delete next[taskId];
            return next;
        });
    };

    const updateTask = (taskId, nextTask) => {
        setTasks(tasks.map((task) => (task.id === taskId ? nextTask : task)));
    };

    const clearTaskError = (taskId, field, rowId) => {
        setTaskErrors((prev) => {
            const currentTaskErrors = prev?.[taskId];
            if (
                !currentTaskErrors ||
                (!currentTaskErrors.taskName && !currentTaskErrors.taskTypes)
            ) {
                return prev;
            }
            const nextTaskErrors = { ...currentTaskErrors };
            if (field === 'taskName' && nextTaskErrors.taskName) delete nextTaskErrors.taskName;
            if (rowId != null && nextTaskErrors.taskTypes && nextTaskErrors.taskTypes[rowId]) {
                const rowErr = { ...nextTaskErrors.taskTypes[rowId] };
                delete rowErr[field];
                const updatedTypes = { ...nextTaskErrors.taskTypes };
                if (Object.keys(rowErr).length === 0) {
                    delete updatedTypes[rowId];
                } else {
                    updatedTypes[rowId] = rowErr;
                }
                nextTaskErrors.taskTypes = updatedTypes;
            }

            const hasErrors =
                !!nextTaskErrors.taskName ||
                Object.keys(nextTaskErrors.taskTypes || {}).length > 0;
            const next = { ...prev };
            if (hasErrors) {
                next[taskId] = nextTaskErrors;
            } else {
                delete next[taskId];
            }
            return next;
        });
    };

    const handleFinish = () => {
        const validations = tasks.map((task) => ({
            taskId: task.id,
            result: validateTask(task),
        }));
        const invalidTasks = validations.filter(({ result }) => !result.ok);

        if (invalidTasks.length > 0) {
            setTaskErrors(
                invalidTasks.reduce((acc, { taskId, result }) => {
                    acc[taskId] = result.errors;
                    return acc;
                }, {})
            );
            setStep2Summary(
                invalidTasks.length === 1
                    ? invalidTasks[0].result.summary || 'Please fix the highlighted fields'
                    : `Please fix the highlighted fields in ${invalidTasks.length} tasks`
            );
            return;
        }
        setTaskErrors({});
        setStep2Summary('');

        const projectData = {
            group_name: sanitizeProjectName(projectName, { trimEnd: true }),
            project_goal: projectGoal,
            tasks: tasks.flatMap((task) => buildTasksPayload(task)),
        };

        onCreateProject(projectData);
        resetForm();
        onClose();
    };

    const handleCancel = () => {
        onClose();
        resetForm();
    };

    const renderStep1 = () => (
        <div>
            <div style={{ fontWeight: 500, marginBottom: 6 }}>
                Project Name <span style={{ color: '#F87171' }}>*</span>
            </div>
            <Input
                placeholder="Add project name e.g 'Legal Document Reviewer'"
                value={projectName}
                onChange={(e) => {
                    const value = sanitizeProjectName(e.target.value);
                    setProjectName(value);
                    setProjectNameError(false);
                }}
                style={{ marginBottom: projectNameError ? 4 : 18, height: 40 }}
                status={projectNameError ? 'error' : ''}
            />
            {projectNameError && (
                <div style={{ color: '#F87171', marginBottom: 14, fontSize: 13 }}>
                    Project Name is required
                </div>
            )}
            <div style={{ fontWeight: 500, marginBottom: 6 }}>Project Goal</div>
            <TextArea
                placeholder="Write about the project e.g 'Build a distilled LLM that understands and flags issues in NDAs.'"
                value={projectGoal}
                onChange={(e) => setProjectGoal(e.target.value)}
                style={{ marginBottom: 24, minHeight: 80, resize: 'none' }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <Button onClick={handleCancel} style={{ height: 38 }}>
                    Cancel
                </Button>
                <Button
                    type="primary"
                    onClick={handleNextStep}
                    style={{ height: 38, background: '#2563EB' }}
                >
                    Next
                </Button>
            </div>
        </div>
    );

    const renderTask = (task, index) => (
        <div
            key={task.id}
            style={{
                borderRadius: 8,
                padding: 16,
                marginBottom: 16,
                position: 'relative',
            }}
        >
            {tasks.length > 1 && (
                <Button
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={() => removeTask(task.id)}
                    aria-label={`Remove task ${index + 1}`}
                    style={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        color: '#EF4444',
                        padding: 4,
                        minWidth: 'auto',
                    }}
                />
            )}
            <div style={{ fontWeight: 500, marginBottom: 16, fontSize: 14, color: '#374151' }}>
                Task {index + 1}
            </div>
            <TaskComposer
                task={task}
                onChange={(nextTask) => updateTask(task.id, nextTask)}
                errors={taskErrors[task.id] || {}}
                onClearError={(field, rowId) => clearTaskError(task.id, field, rowId)}
                taskNamePlaceholder="Add task name e.g Clause Classification"
            />
        </div>
    );

    const renderStep2 = () => (
        <div>
            <TaskModalScrollArea maxHeight="65vh">
                {tasks.map((task, index) => renderTask(task, index))}
                <div
                    className="fs-unmask"
                    role="button"
                    tabIndex={0}
                    onClick={addTask}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            addTask();
                        }
                    }}
                    style={{
                        color: colors.buttonsBlue,
                        fontSize: 15,
                        marginBottom: 32,
                        cursor: 'pointer',
                        userSelect: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                    }}
                >
                    <span style={{ fontSize: 18 }}>+</span>
                    Add another task
                </div>
                {step2Summary && (
                    <div style={{ color: '#F87171', marginBottom: 16, fontSize: 13 }}>
                        {step2Summary}
                    </div>
                )}
            </TaskModalScrollArea>
            <div
                className="fs-unmask"
                style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}
            >
                <Button onClick={handlePrevStep} style={{ height: 38 }}>
                    Back
                </Button>
                <Button onClick={handleCancel} style={{ height: 38 }}>
                    Cancel
                </Button>
                <ReadOnlyGuard>
                    <Button
                        type="primary"
                        onClick={handleFinish}
                        style={{ height: 38, background: '#2563EB', color: '#fff' }}
                    >
                        Create
                    </Button>
                </ReadOnlyGuard>
            </div>
        </div>
    );

    const stepLabels = useMemo(
        () => [
            <span key="s1" className="fs-unmask">
                Project Details
            </span>,
            <span key="s2" className="fs-unmask">
                Tasks Inside the Project
            </span>,
        ],
        []
    );

    return (
        <StepsModal
            visible={isOpen}
            onCancel={handleCancel}
            title={<span className="fs-unmask">Create a New Project</span>}
            subtitle={
                <span className="fs-unmask">
                    Define your project's scope and start organizing tasks.
                </span>
            }
            steps={stepLabels}
            currentStep={step}
            onStepChange={setStep}
            width={620}
        >
            {step === 0 ? renderStep1() : renderStep2()}
        </StepsModal>
    );
};
